import { ReactNode, useMemo, useState } from "react";
import { AllStats, OverviewStat, PlayerColorInfo, StatValue } from "src/components/types";
import "./ResultTable.css";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { getCgoColor } from "src/components/playerColors";
import { stripWidthFromStyle } from "src/components/gametable/redesign/utility";
import { UsernameDisplay } from "../pages/UserPage";

function ResultTable(props: { allStats: AllStats; playerColors: PlayerColorInfo[]; overviewStats?: OverviewStat[] }) {
  let orderedStats: Set<string> = new Set();
  let otherStatsSet: Set<string> = new Set();
  let statOrder = props.overviewStats?.map((os) => os.name) ?? [];

  for (let statName in props.allStats.playerStats) {
    if (statOrder.includes(statName)) {
      orderedStats.add(statName);
    } else {
      otherStatsSet.add(statName);
    }
  }

  let finalStatOrder = statOrder.filter((stat) => orderedStats.has(stat));
  let otherStats: string[] = [...otherStatsSet].sort();

  let header = [
    <TableCell key="catheader" className="result-table-header" component="th">
      Category
    </TableCell>,
  ];

  for (let pci of props.playerColors) {
    header.push(
      <TableCell
        style={{ color: getCgoColor(pci.color) }}
        key={"colorheader_" + pci.color}
        className="result-table-header playername-header"
        component="th"
      >
        <UsernameDisplay username={pci.userInfo?.username ?? "dummy-" + pci.color}></UsernameDisplay>
      </TableCell>,
    );
  }

  function generateStatRow(statName: string, rows: ReactNode[]) {
    let matchingOverviewStat = props.overviewStats?.find((stat) => stat.name == statName);
    let cells = [
      <TableCell key={"stat-header-" + statName} component="th">
        {matchingOverviewStat?.displayName || statName}
      </TableCell>,
    ];
    for (let pci of props.playerColors) {
      let cellValue: StatValue = "";
      let cellStyle = stripWidthFromStyle(matchingOverviewStat?.customStyle || {});
      if (props.allStats.playerStats[statName]) {
        cellValue = props.allStats.playerStats[statName][pci.color] ?? "";
      }
      if (Array.isArray(cellValue)) {
        cellValue = cellValue.join(", ");
      }
      cells.push(
        <TableCell key={"stat-" + pci.color + "-" + statName} style={cellStyle}>
          {cellValue}
        </TableCell>,
      );
    }
    rows.push(<TableRow key={"stat-row-" + statName}>{cells}</TableRow>);
  }

  let overviewRows = [] as ReactNode[];
  for (let statName of finalStatOrder) {
    generateStatRow(statName, overviewRows);
  }

  let otherStatRows = [] as ReactNode[];
  for (let statName of otherStats) {
    generateStatRow(statName, otherStatRows);
  }

  let globalRows = useMemo(() => {
    let result = [];
    let globalStatOrder = Object.keys(props.allStats?.tableStats ?? {});
    globalStatOrder.sort();
    for (let statName of globalStatOrder) {
      let currValue = props.allStats.tableStats[statName];
      let textContent = "";
      if (Array.isArray(currValue)) {
        textContent = currValue.join(", ");
      } else {
        textContent = "" + currValue;
      }
      let newRow = (
        <TableRow key={statName}>
          <TableCell>{statName}</TableCell>
          <TableCell>{textContent}</TableCell>
        </TableRow>
      );
      result.push(newRow);
    }
    return result;
  }, [props.allStats.tableStats]);

  let objectRows = useMemo(() => {
    let result = [];
    let globalStatOrder = Object.keys(props.allStats?.objectStats ?? ({} as any));
    globalStatOrder.sort();
    for (let objectName of globalStatOrder) {
      let newRow = (
        <TableRow key={objectName}>
          <TableCell>{objectName}</TableCell>
          <TableCell>
            {Object.entries(props.allStats.objectStats[objectName] ?? {})
              .map((entry) => {
                return entry[0] + ":" + entry[1];
              })
              .join("; ")}
          </TableCell>
        </TableRow>
      );
      result.push(newRow);
    }
    return result;
  }, [props.allStats.objectStats]);

  let objectStats = (
    <div className="stats-container">
      <h3>Component stats</h3>
      <TableContainer sx={{ width: "70%" }}>
        <Table className="object-stat-table" size="small">
          <TableBody>{objectRows}</TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  let globalStats = (
    <div className="stats-container">
      <h3>Global stats</h3>
      <TableContainer sx={{ width: "70%" }}>
        <Table className="global-stat-table" size="small">
          <TableBody>{globalRows}</TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  return (
    <div className="result-table-wrap">
      <TableContainer sx={{ width: "70%", flex: "1", overflow: "unset" }}>
        <Table className="result-table">
          <TableHead sx={{ position: "sticky", top: "65px" }}>
            <TableRow key="headerrow">{header}</TableRow>
          </TableHead>
          <TableBody>
            {overviewRows}
            {otherStatRows}
          </TableBody>
        </Table>
      </TableContainer>
      {objectStats}
      {globalStats}
    </div>
  );
}

export default ResultTable;
